import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {InputTextModule} from "primeng/inputtext";
import {ButtonModule} from "primeng/button";
import {FloatLabelModule} from "primeng/floatlabel";
import {MessageModule} from "primeng/message";
import {InputNumberModule} from "primeng/inputnumber";
import {SelectModule} from "primeng/select";
import {InputMaskModule} from "primeng/inputmask";
import {ProgressSpinnerModule} from "primeng/progressspinner";
import {ToggleSwitchModule} from "primeng/toggleswitch";
import {Subject, Subscription} from "rxjs";
import {EbayMarketplace} from "@app/core/interfaces/ebay";
import {DynamicDialogRef} from "primeng/dynamicdialog";
import {MessageService} from "primeng/api";
import {AutoFocusModule} from "primeng/autofocus";
import {TooltipModule} from "primeng/tooltip";
import {EbayService} from "@app/core/services";
import {TextareaModule} from "primeng/textarea";

@Component({
    selector: 'app-ebay-shop-form',
    imports: [
        CommonModule,
        FormsModule,
        InputTextModule,
        ButtonModule,
        FloatLabelModule,
        MessageModule,
        InputNumberModule,
        SelectModule,
        InputMaskModule,
        ProgressSpinnerModule,
        ToggleSwitchModule,
        AutoFocusModule,
        TooltipModule,
        TextareaModule
    ],
    templateUrl: './ebay-shop-form.html'
})
export class EbayShopForm implements OnDestroy, OnInit {
    @Input() ebayMarketplace!: Partial<EbayMarketplace>;

    protected apiCall$?: Subscription;
    private destroy$: Subject<any> = new Subject();
    formMarketplace: Partial<EbayMarketplace> = {};
    aspectsKeys: string[] = ['marque', 'mpn', 'pays_origine'];

    isLoadingData: boolean = true;
    isSendingDatas: boolean = false;


    constructor(
        private dialogRef: DynamicDialogRef,
        private messageService: MessageService,
        private ebayService: EbayService
    ) {}

    ngOnInit(): void {
        // Store a deep copy of the original stock values to detect changes later
        this.formMarketplace = JSON.parse(JSON.stringify(this.ebayMarketplace));
        this.setAspectsKeysValues();
        // console.log("Form Marketplace:", this.formMarketplace);
        this.isLoadingData = false;
    }

    setAspectsKeysValues(): void {
        if (!this.formMarketplace.aspects_noms_key_value) {
            this.formMarketplace.aspects_noms_key_value = [];
        }

        if (this.formMarketplace.aspects_noms && Object.keys(this.formMarketplace.aspects_noms).length > 0) {
            for (const [key, value] of Object.entries(this.formMarketplace.aspects_noms)) {
                const existingIndex = this.formMarketplace.aspects_noms_key_value.findIndex(item => item.key === key);
                if (existingIndex !== -1) {
                    this.formMarketplace.aspects_noms_key_value[existingIndex].value = value[0];
                } else {
                    this.formMarketplace.aspects_noms_key_value.push({ key: key, value: value[0] });
                }
            }
        }
    }

    setAspectsFromKeyValue(): void {
        if (this.formMarketplace.aspects_noms_key_value) {
            this.formMarketplace.aspects_noms = {};
            for (const item of this.formMarketplace.aspects_noms_key_value) {
                if (item.key && item.value) {
                    this.formMarketplace.aspects_noms[item.key] = [item.value];
                }
            }
        }
    }

    addAspect() {
        this.formMarketplace.aspects_noms_key_value = this.formMarketplace.aspects_noms_key_value || [];
        this.formMarketplace.aspects_noms_key_value.push({ key: '', value: '' });
    }

    removeAspect(index: number) {
        if (this.formMarketplace.aspects_noms_key_value) {
            this.formMarketplace.aspects_noms_key_value.splice(index, 1);
        }
        if (this.formMarketplace.aspects_noms_key_value && this.formMarketplace.aspects_noms_key_value.length === 0) {
            delete this.formMarketplace.aspects_noms_key_value;
        }
    }

    addDeliveryPolicy() {
        this.formMarketplace.politiques_livraison = this.formMarketplace.politiques_livraison || [];
        this.formMarketplace.politiques_livraison.push({ policy_id: '', poids_max: '' });
    }

    removeDeliveryPolicy(index: number) {
        if (this.formMarketplace.politiques_livraison) {
            this.formMarketplace.politiques_livraison.splice(index, 1);
        }
        if (this.formMarketplace.politiques_livraison && this.formMarketplace.politiques_livraison.length === 0) {
            delete this.formMarketplace.politiques_livraison;
        }
    }

    hideDialog() {
        this.isSendingDatas = false;
        this.dialogRef?.close();
    }

    private getChangedFields(withoutKeyValue: boolean = true): Partial<EbayMarketplace> {
        const changes: Record<string, unknown> = {};
        this.setAspectsFromKeyValue();
        const keys = Object.keys(this.formMarketplace) as Array<keyof EbayMarketplace>;

        for (const key of keys) {
            const currentValue = this.formMarketplace[key];
            const originalValue = this.ebayMarketplace[key];

            if (JSON.stringify(currentValue) !== JSON.stringify(originalValue)) {
                changes[key as string] = currentValue;
            }
        }

        if (withoutKeyValue && changes['aspects_noms_key_value']) {
            delete changes['aspects_noms_key_value'];
        }

        return changes as Partial<EbayMarketplace>;
    }

    hasChanges(): boolean {
        // If creating a new stock (no id), always allow saving
        if (!this.formMarketplace.id) {
            return true;
        }
        // If editing, check if there are any changes
        const changes = this.getChangedFields();
        // console.log("Detected Changes:", changes);
        return Object.keys(changes).length > 0;
    }

    public canSubmit(): boolean {
        return this.hasChanges() && !this.isSendingDatas;
    }

    onSubmit() {
        this.isSendingDatas = true;
        this.setAspectsFromKeyValue();
        // console.log("Submitting Marketplace Data:", this.formMarketplace);
        const changes = this.getChangedFields();
        if(changes.aspects_noms_key_value) {
         delete changes.aspects_noms_key_value;
        }
        // console.log("Detected Changes:", changes);

        this.apiCall$?.unsubscribe();
        this.apiCall$ = this.ebayService.patchEbayMarketplace(this.formMarketplace.marketplace_id as string | number, changes)
            .subscribe({
                next: (result) => {
                    // console.log("Marketplace updated successfully:", result);
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Marketplace updated successfully.' });
                    this.isSendingDatas = false;
                    this.dialogRef?.close(result);
                },
                error: (error) => {
                    // console.error("Error updating marketplace:", error);
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update marketplace.' });
                    this.isSendingDatas = false;
                }
            });
    }

    ngOnDestroy(): void {
        if (this.destroy$) {
            this.destroy$.next(true);
            this.destroy$.complete();
        }
        if(this.apiCall$) {
            this.apiCall$.unsubscribe();
        }
    }
}
